import gsap from "gsap";

const imageNames = [
  "NAM_2109-small.jpg",
  "NAM_2259-small.jpg",
  "NAM_2630-small.jpg",
  "NAM_2340-small.jpg",
  "NAM_2788-small.jpg",
  "NAM_2840-small.jpg",
];

export function loadGalleryImages() {
  const fragment = document.createDocumentFragment();

  imageNames.slice(0, 6).forEach((imageName, index) => {
    const figure = document.createElement("figure");
    figure.classList.add(
      "wedding-gallery__item",
      "shadow-8dp",
      `wedding-gallery__item--${index + 1}`
    );
    const img = document.createElement("img");
    img.classList.add("wedding-gallery__image");
    img.src = `${window.location.origin}/${imageName}`;
    img.alt = imageName;
    figure.appendChild(img);
    fragment.appendChild(figure);
  });

  const grid = document.querySelector(".wedding-gallery");
  grid.appendChild(fragment);

  // Set up click handlers for each gallery image
  setupImageLightbox();
}

function setupImageLightbox() {
  // Create lightbox elements if they don't exist
  if (!document.querySelector(".lightbox-overlay")) {
    createLightboxElements();
  }

  // Get lightbox elements
  const lightboxOverlay = document.querySelector(".lightbox-overlay");
  const lightboxImage = document.querySelector(".lightbox-image");
  const closeButton = document.querySelector(".lightbox-close");
  const prevButton = document.querySelector(".lightbox-prev");
  const nextButton = document.querySelector(".lightbox-next");

  // Track current image index
  let currentImageIndex = 0;
  const galleryItems = document.querySelectorAll(".wedding-gallery__item");

  // Add click handlers to all gallery items
  document
    .querySelectorAll(".wedding-gallery__item")
    .forEach((figure, index) => {
      figure.addEventListener("click", () => {
        // Set current image index
        currentImageIndex = index;

        // Display the image
        showImageAtIndex(currentImageIndex);
      });
    });

  // Function to show image at specified index
  function showImageAtIndex(index) {
    const figure = galleryItems[index];
    const imgSrc = figure.querySelector("img").src;

    // Use higher quality image for lightbox (medium instead of small)
    const highQualityImgSrc = imgSrc.replace("-small.jpg", "-medium.jpg");

    // Set the lightbox image source
    lightboxImage.src = highQualityImgSrc;

    // Update navigation button visibility
    updateNavigationVisibility();

    // Show the lightbox with animation
    gsap.set(lightboxOverlay, { display: "flex" });
    gsap.fromTo(lightboxOverlay, { opacity: 0 }, { opacity: 1, duration: 0.3 });

    // Animate the image
    gsap.fromTo(
      lightboxImage,
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }
    );
  }

  // Update navigation button visibility based on current index
  function updateNavigationVisibility() {
    // Show/hide previous button based on whether there's a previous image
    if (currentImageIndex > 0) {
      prevButton.style.display = "flex";
    } else {
      prevButton.style.display = "none";
    }

    // Show/hide next button based on whether there's a next image
    if (currentImageIndex < galleryItems.length - 1) {
      nextButton.style.display = "flex";
    } else {
      nextButton.style.display = "none";
    }
  }

  // Navigate to previous image
  prevButton.addEventListener("click", (e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      navigateToImage(currentImageIndex - 1);
    }
  });

  // Navigate to next image
  nextButton.addEventListener("click", (e) => {
    e.stopPropagation();
    if (currentImageIndex < galleryItems.length - 1) {
      navigateToImage(currentImageIndex + 1);
    }
  });

  // Function to navigate between images with animation
  function navigateToImage(index) {
    // Store the direction for animation
    const direction = index > currentImageIndex ? 1 : -1;
    currentImageIndex = index;

    // Animate current image out
    gsap.to(lightboxImage, {
      opacity: 0,
      x: -30 * direction,
      duration: 0.2,
      onComplete: () => {
        // Change the image source
        const figure = galleryItems[currentImageIndex];
        const imgSrc = figure.querySelector("img").src;
        const highQualityImgSrc = imgSrc.replace("-small.jpg", "-medium.jpg");
        lightboxImage.src = highQualityImgSrc;

        // Update navigation
        updateNavigationVisibility();

        // Animate new image in
        gsap.fromTo(
          lightboxImage,
          { opacity: 0, x: 30 * direction },
          { opacity: 1, x: 0, duration: 0.3 }
        );
      },
    });
  }

  // Close lightbox when clicking the close button
  closeButton.addEventListener("click", closeLightbox);

  // Close lightbox when clicking outside the image
  lightboxOverlay.addEventListener("click", (e) => {
    if (e.target === lightboxOverlay) {
      closeLightbox();
    }
  });

  // Keyboard navigation
  document.addEventListener("keydown", (e) => {
    if (!lightboxOverlay || lightboxOverlay.style.display === "none") {
      return;
    }

    switch (e.key) {
      case "Escape":
        closeLightbox();
        break;
      case "ArrowLeft":
        if (currentImageIndex > 0) {
          navigateToImage(currentImageIndex - 1);
        }
        break;
      case "ArrowRight":
        if (currentImageIndex < galleryItems.length - 1) {
          navigateToImage(currentImageIndex + 1);
        }
        break;
    }
  });

  function closeLightbox() {
    gsap.to(lightboxOverlay, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => {
        gsap.set(lightboxOverlay, { display: "none" });
      },
    });
  }
}

function createLightboxElements() {
  // Create overlay
  const overlay = document.createElement("div");
  overlay.classList.add("lightbox-overlay");

  // Create image container
  const imageContainer = document.createElement("div");
  imageContainer.classList.add("lightbox-container");

  // Create image
  const image = document.createElement("img");
  image.classList.add("lightbox-image");

  // Create navigation buttons
  const prevBtn = document.createElement("button");
  prevBtn.classList.add("lightbox-nav", "lightbox-prev");
  prevBtn.innerHTML = "&#10094;";
  prevBtn.setAttribute("aria-label", "Previous image");

  const nextBtn = document.createElement("button");
  nextBtn.classList.add("lightbox-nav", "lightbox-next");
  nextBtn.innerHTML = "&#10095;";
  nextBtn.setAttribute("aria-label", "Next image");

  // Create close button
  const closeBtn = document.createElement("button");
  closeBtn.classList.add("lightbox-close");
  closeBtn.innerHTML = "&times;";
  closeBtn.setAttribute("aria-label", "Close lightbox");

  // Append elements
  imageContainer.appendChild(image);
  overlay.appendChild(imageContainer);
  overlay.appendChild(prevBtn);
  overlay.appendChild(nextBtn);
  overlay.appendChild(closeBtn);
  document.body.appendChild(overlay);

  // Add default styles for lightbox
  const style = document.createElement("style");
  style.textContent = `
    .lightbox-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.45);
      z-index: 1000;
      display: none;
      justify-content: center;
      align-items: center;
    }
    
    .lightbox-container {
      position: relative;
      max-width: 90%;
      max-height: 90%;
    }
    
    .lightbox-image {
      max-width: 100%;
      max-height: 90vh;
      border: 4px solid #ffffff;
      box-shadow: 0 5px 30px rgba(0, 0, 0, 0.3);
    }
    
    .lightbox-close {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      background-color: rgba(255, 255, 255, 0.2);
      border: none;
      border-radius: 50%;
      color: white;
      font-size: 24px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .lightbox-close:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }
    
    .lightbox-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 50px;
      height: 50px;
      background-color: rgba(255, 255, 255, 0.15);
      border: none;
      border-radius: 50%;
      color: white;
      font-size: 24px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.3s, transform 0.2s;
    }
    
    .lightbox-nav:hover {
      background-color: rgba(255, 255, 255, 0.3);
      transform: translateY(-50%) scale(1.1);
    }
    
    .lightbox-prev {
      left: 20px;
    }
    
    .lightbox-next {
      right: 20px;
    }
  `;
  document.head.appendChild(style);
}
